/**
 * Create a map pin marker element (button) with an SVG teardrop shape.
 * The icon is applied via CSS background-image on the .marker-pin-icon element.
 *
 * @param {string} className - CSS class for the marker type (e.g. 'marker-layer-dining')
 * @param {string} color - Fill color for the pin (e.g. '#00759b')
 * @param {string} label - Accessible label for the button
 * @returns {HTMLButtonElement}
 */
export default function createMarkerPin(className, color, label) {
	const el = document.createElement('button');
	el.className = 'marker-layer ' + className;
	el.setAttribute('aria-label', label);

	el.innerHTML =
		`<svg class="marker-pin-shape" viewBox="-1.5 -1.5 33 44" xmlns="http://www.w3.org/2000/svg">` +
		`<path d="M15 0C6.72 0 0 6.72 0 15 0 26.08 15 41 15 41S30 26.08 30 15C30 6.72 23.28 0 15 0z" fill="${color}" stroke="#fff" stroke-width="1.5"/>` +
		`</svg>` +
		`<span class="marker-pin-icon"></span>` +
		`<span class="marker-pin-label" aria-hidden="true">${label}</span>`;

	return el;
}

/**
 * Sort GeoJSON point features by proximity to the fixed southeast corner of the
 * map's overall bounds, nearest first. Mutates and returns the array.
 *
 * Marker tab order follows DOM (creation) order, so sorting features before
 * building their markers makes keyboard focus walk them spatially: the first Tab
 * lands on the pin closest to the southeast corner, then steps outward to the
 * farthest. The reference corner is the map's static maxBounds — not the live
 * viewport — so the order is deterministic regardless of the current pan/zoom.
 *
 * A degree of longitude is shorter than a degree of latitude away from the
 * equator, so the longitude delta is scaled by cos(lat) to rank by true
 * on-the-ground distance rather than raw degrees.
 *
 * Pass `groupKey` to keep features that share a key (e.g. every dining location
 * in one building) consecutive in the tab order: groups are ranked by the corner
 * distance of their centroid — the mean of their members' coordinates — and
 * members within a group are nearest-first among themselves. This walks one
 * building's markers fully before moving to the next instead of interleaving
 * nearby buildings. Without it, features are ranked individually.
 *
 * @param {Array<{geometry: {coordinates: [number, number]}}>} features
 * @param {(feature) => (string|number|null)} [groupKey] optional grouping accessor
 * @returns {Array} the same array, reordered in place
 */
export function sortBySoutheastProximity(features, groupKey) {
	const se = W.map.getMaxBounds().getSouthEast(); // fixed SE corner of map bounds
	const cosLat = Math.cos((se.lat * Math.PI) / 180);

	const distSq = ([lng, lat]) => {
		const dx = (lng - se.lng) * cosLat;
		const dy = lat - se.lat;
		return dx * dx + dy * dy;
	};
	const byCorner = (a, b) => distSq(a.geometry.coordinates) - distSq(b.geometry.coordinates);

	if (!groupKey) {
		return features.sort(byCorner);
	}

	// Bucket features by group key so same-building markers stay together. A
	// missing key makes the feature its own group (ranked like an individual).
	const groups = new Map();
	features.forEach((feature, i) => {
		const key = groupKey(feature) ?? `__ungrouped_${i}__`;
		if (!groups.has(key)) groups.set(key, []);
		groups.get(key).push(feature);
	});

	// Rank groups by the corner distance of their centroid; order members within.
	const ordered = [...groups.values()]
		.map((members) => {
			members.sort(byCorner);
			const n = members.length;
			const cx = members.reduce((sum, f) => sum + f.geometry.coordinates[0], 0) / n;
			const cy = members.reduce((sum, f) => sum + f.geometry.coordinates[1], 0) / n;
			return { members, dist: distSq([cx, cy]) };
		})
		.sort((a, b) => a.dist - b.dist)
		.flatMap((group) => group.members);

	features.splice(0, features.length, ...ordered); // reorder in place; keep caller's reference
	return features;
}

/**
 * Compute display coordinates that fan out markers sharing an identical location,
 * so a stack of co-located pins (e.g. several libraries in one building) reveals
 * there is more than one instead of hiding under a single pin.
 *
 * Returns a Map keyed by the feature object; features with no collision are
 * absent, so callers fall back to the feature's own coordinate. The fan is built
 * in ground metres and converted to lng/lat, so — unlike a fixed pixel offset —
 * it scales with zoom: pins splay apart as you zoom in and converge back over the
 * building as you zoom out, never floating off it. The map is north-up with
 * rotation disabled, so the upward arc maps to a northward arc on the ground.
 *
 * Source data is untouched; this only changes where each pin is drawn.
 *
 * @param {Array<{geometry: {coordinates: [number, number]}}>} features
 * @returns {Map<object, [number, number]>} feature → display [lng, lat]
 */
export function coincidentMarkerCoords(features) {
	const COORD_PRECISION = 6; // ~0.1 m; identical source coords compare equal
	const GAP_DEG = 50; // angular gap between adjacent pins in the fan
	const MAX_SPREAD_DEG = 170; // cap so the bouquet never wraps under the point
	const METERS_PER_DEG_LAT = 111320; // close enough for a few-metre nudge

	const buckets = new Map();
	features.forEach((feature) => {
		const [lng, lat] = feature.geometry.coordinates;
		const key = `${lng.toFixed(COORD_PRECISION)},${lat.toFixed(COORD_PRECISION)}`;
		if (!buckets.has(key)) buckets.set(key, []);
		buckets.get(key).push(feature);
	});

	const coords = new Map();
	buckets.forEach((group) => {
		const n = group.length;
		if (n < 2) return; // a lone pin needs no offset

		const spreadDeg = Math.min(MAX_SPREAD_DEG, GAP_DEG * (n - 1));
		const radiusM = 8 + 3 * n; // ground metres from the shared point; grows with the pile
		group.forEach((feature, i) => {
			const [lng, lat] = feature.geometry.coordinates;
			// Angle from due north (0 = north), positive toward the east.
			const deg = -spreadDeg / 2 + (spreadDeg * i) / (n - 1);
			const rad = (deg * Math.PI) / 180;
			const eastM = radiusM * Math.sin(rad);
			const northM = radiusM * Math.cos(rad);
			const dLng = eastM / (METERS_PER_DEG_LAT * Math.cos((lat * Math.PI) / 180));
			const dLat = northM / METERS_PER_DEG_LAT;
			coords.set(feature, [lng + dLng, lat + dLat]);
		});
	});

	return coords;
}

// Page chrome that overlays the map and can hide a focused marker. The menu is
// a full-width top sheet on mobile and a left-docked panel on desktop; the site
// header is a full-width band at the top when shown.
const MAP_UI_SELECTORS = ['.map-menu', '#branding'];

/**
 * Measure how far page chrome intrudes over each edge of the map container.
 *
 * Each overlapping element is "docked" to an edge: full-width elements to the
 * top/bottom, narrower panels to the left/right. Returns the map container rect
 * plus the largest pixel inset found on each side.
 *
 * @returns {{ map: DOMRect, inset: {top: number, right: number, bottom: number, left: number} }}
 */
function chromeInsets() {
	const map = W.map.getContainer().getBoundingClientRect();
	const inset = { top: 0, right: 0, bottom: 0, left: 0 };

	MAP_UI_SELECTORS.forEach((selector) => {
		const el = document.querySelector(selector);
		if (!el) return;

		const r = el.getBoundingClientRect();
		const overlapW = Math.min(r.right, map.right) - Math.max(r.left, map.left);
		const overlapH = Math.min(r.bottom, map.bottom) - Math.max(r.top, map.top);
		if (overlapW <= 0 || overlapH <= 0) return; // hidden or not over the map

		if (overlapW >= 0.7 * map.width) {
			// Full-width band: docks to the top or bottom edge.
			if ((r.top + r.bottom) / 2 < map.top + map.height / 2) {
				inset.top = Math.max(inset.top, r.bottom - map.top);
			} else {
				inset.bottom = Math.max(inset.bottom, map.bottom - r.top);
			}
		} else {
			// Narrower panel: docks to the left or right edge.
			if ((r.left + r.right) / 2 < map.left + map.width / 2) {
				inset.left = Math.max(inset.left, r.right - map.left);
			} else {
				inset.right = Math.max(inset.right, map.right - r.left);
			}
		}
	});

	return { map, inset };
}

/**
 * Pan the map to reveal a marker when it receives keyboard focus.
 *
 * Don't pan for pointer focus (so clicking pin doesn't trigger a surprise pan),
 * for markers already visible within the unobstructed area, and honors
 * reduced-motion. A marker that is off-screen *or* hidden behind the menu panel
 * or header is eased into the clear region, satisfying WCAG 2.2 SC 2.4.11
 * (Focus Not Obscured) and 2.4.7 (Focus Visible).
 *
 * @param {HTMLElement} markerEl - The marker button element.
 * @param {[number, number]} lngLat - The marker's [lng, lat] coordinates.
 */
export function panIntoViewOnFocus(markerEl, lngLat) {
	markerEl.addEventListener('focus', () => {
		// Only react to keyboard focus, not pointer clicks.
		if (!markerEl.matches(':focus-visible')) return;

		// Focusing an off-screen marker makes the browser scroll the map
		// container (it is overflow:hidden but still programmatically
		// scrollable) to reveal it, shifting the canvas. Undo that — we move
		// the camera instead.
		const container = W.map.getContainer();
		container.scrollLeft = 0;
		container.scrollTop = 0;

		const { map, inset } = chromeInsets();
		const pad = 16; // comfort margin from the edges and chrome

		// Project the marker to container pixels. Unlike getBoundingClientRect,
		// this reflects the map's geographic transform and is unaffected by any
		// stray DOM scroll, so off-screen markers report off-screen positions.
		const pos = W.map.project(lngLat);

		// Already within the unobstructed area? Nothing to do.
		const visible =
			pos.x >= inset.left + pad &&
			pos.x <= map.width - inset.right - pad &&
			pos.y >= inset.top + pad &&
			pos.y <= map.height - inset.bottom - pad;
		if (visible) return;

		// Center the marker within the unobstructed area (offset is not persisted).
		const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		W.map.easeTo({
			center: lngLat,
			offset: [(inset.left - inset.right) / 2, (inset.top - inset.bottom) / 2],
			duration: reduceMotion ? 0 : 400,
		});
	});
}
